/**
 * Code generation for member codes and lobby codes.
 *
 * A member code is both the door key and the identity: entering it on any
 * device signs you in as you. That means it has to survive being typed on a
 * phone and read aloud over Discord, so it is words rather than characters.
 */
// Chosen to be unambiguous when spoken and quick to type. No homophones, no
// words that differ only by a plural, nothing with tricky spelling.
const ADJECTIVES = [
    'amber', 'ancient', 'arctic', 'bitter', 'blazing', 'bold', 'brass', 'bright',
    'bronze', 'calm', 'candid', 'cedar', 'clever', 'cobalt', 'copper', 'cosmic',
    'crimson', 'crisp', 'crystal', 'dapper', 'dusty', 'eager', 'early', 'electric',
    'ember', 'fabled', 'fearless', 'feral', 'fierce', 'flint', 'frosty', 'gentle',
    'gilded', 'glowing', 'golden', 'granite', 'happy', 'hidden', 'hollow', 'humble',
    'indigo', 'ivory', 'jagged', 'jolly', 'lively', 'lucky', 'lunar', 'marble',
    'mellow', 'mighty', 'misty', 'molten', 'noble', 'northern', 'olive', 'onyx',
    'opal', 'patient', 'pewter', 'placid', 'polar', 'prime', 'proud', 'quiet',
    'rapid', 'restless', 'rugged', 'rustic', 'sable', 'sapphire', 'scarlet', 'secret',
    'silent', 'silver', 'solar', 'stellar', 'stormy', 'sunny', 'swift', 'tidal',
    'timber', 'tranquil', 'velvet', 'vivid', 'wandering', 'wild', 'winter', 'zealous',
];
const NOUNS = [
    'anchor', 'anvil', 'arrow', 'badger', 'beacon', 'bison', 'bramble', 'canyon',
    'cardinal', 'cavern', 'cedar', 'comet', 'compass', 'condor', 'coral', 'crane',
    'dagger', 'delta', 'dolphin', 'dragon', 'ember', 'falcon', 'fathom', 'ferret',
    'forge', 'fossil', 'garnet', 'geyser', 'glacier', 'gopher', 'harbor', 'harvest',
    'heron', 'hollow', 'ibex', 'jaguar', 'kestrel', 'lantern', 'ledger', 'lighthouse',
    'lynx', 'magnet', 'mammoth', 'marlin', 'meadow', 'meteor', 'monsoon', 'mustang',
    'nebula', 'orbit', 'osprey', 'otter', 'panther', 'pelican', 'phoenix', 'pillar',
    'prairie', 'quarry', 'quasar', 'raven', 'reef', 'ridge', 'river', 'rocket',
    'saffron', 'sailor', 'salmon', 'sequoia', 'sparrow', 'summit', 'tundra', 'turret',
    'vulture', 'walrus', 'warden', 'willow', 'wombat', 'yonder', 'zenith', 'zephyr',
];
/** Every member code starts with this, so a code is recognisable on sight. */
export const CODE_PREFIX = 'ram';
/**
 * Keyspace: 88 adjectives x 80 nouns x 100 = 704,000 combinations.
 *
 * That is not a password, and it is not meant to be. It is paired with
 * per-IP rate limiting on the sign-in route, which is what actually makes
 * guessing impractical. The trade-off buys a code you can read to someone
 * over voice chat without spelling it out.
 */
export function generateMemberCode(random = Math.random) {
    const adjective = ADJECTIVES[Math.floor(random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(random() * NOUNS.length)];
    const digits = String(Math.floor(random() * 100)).padStart(2, '0');
    return `${CODE_PREFIX}-${adjective}-${noun}-${digits}`;
}
/**
 * Accepts whatever the member actually typed. People use spaces, underscores,
 * capitals, and stray whitespace; none of that should be a failed sign-in.
 */
export function normalizeMemberCode(input) {
    return input
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
}
export function isWellFormedMemberCode(input) {
    return /^ram-[a-z]+-[a-z]+-\d{2}$/.test(normalizeMemberCode(input));
}
// ---------------------------------------------------------------------------
// Lobby codes
// ---------------------------------------------------------------------------
/**
 * Ambiguous characters are omitted: no O/0, no I/1/L, no U/V confusion.
 * A lobby code gets read aloud in a Discord call, so legibility beats entropy.
 */
const LOBBY_ALPHABET = 'ABCDEFGHJKMNPQRSTWXYZ23456789';
const LOBBY_CODE_LENGTH = 6;
export function generateLobbyCode(random = Math.random) {
    let out = '';
    for (let i = 0; i < LOBBY_CODE_LENGTH; i++) {
        out += LOBBY_ALPHABET[Math.floor(random() * LOBBY_ALPHABET.length)];
    }
    return out;
}
/**
 * Uppercases and strips separators. Deliberately does NOT try to "correct"
 * look-alike characters: since the alphabet already excludes O, 0, I, 1 and L,
 * a typed O could plausibly mean Q or D, and guessing would risk matching a
 * different lobby than the one intended. A failed code is cheap; joining the
 * wrong lobby is confusing.
 */
export function normalizeLobbyCode(input) {
    return input.trim().toUpperCase().replace(/[\s-]+/g, '');
}
export function isWellFormedLobbyCode(input) {
    const normalized = normalizeLobbyCode(input);
    if (normalized.length !== LOBBY_CODE_LENGTH)
        return false;
    return [...normalized].every((c) => LOBBY_ALPHABET.includes(c));
}
//# sourceMappingURL=codes.js.map